import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Pagination } from '~/components/Pagination';
import { ArtistSearchBar } from '~/modules/artist-roster/components/ArtistSearchBar/ArtistSearchBar';
import { DataError } from '~/modules/artist-roster/components/DataError/DataError';
import { ArtistsTable } from '~/modules/artist-roster/components/Roster/ArtistsTable';
import { useArtistQuery } from '~/modules/artist-roster/hooks/useArtistQuery';
import { useRosterUrlParams } from '~/modules/artist-roster/hooks/useRosterUrlParams';
import { getRosterStatus } from '~/modules/artist-roster/lib/roster';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { useArtistColumns } from '../../hooks/useArtistColumns';
import { getLabels } from '../../lib/labels';
import { type LabelData } from '../../lib/types';
import { ArtistExport } from '../ArtistExport/ArtistExport';
import { ArtistAppliedFilterList } from '../ArtistFilter/ArtistAppliedFilter/ArtistAppliedFilterList';
import { ArtistFilterButton } from '../ArtistFilter/ArtistFilterButton';
import { DEFAULT_ARTIST_FILTERS } from '../ArtistFilter/constants';
import type { ArtistFiltersType } from '../ArtistFilter/types';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { Header } from '../Header';
import type { ArtistAppliedLabelFilter } from './../ArtistFilter/ArtistAppliedFilter/ArtistAppliedFilterList';

export function RosterPage() {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const { columns } = useArtistColumns();
  const [filterPanelIsOpen, setFilterPanelIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  /**
   * @TODO WMG1-426
   * This logic to manage state is repetitious and not scaling well.
   * Investigate whether react-hook-form can be used instead to manage filters.
   */
  const { filters, pageNumber, setFilters, updateFilters, setPageNumber } =
    useRosterUrlParams();
  const { labelIds, searchText, isCrossover, isOnRoster } = filters;

  // These are the full LabelData objects filtered by the current labelIds.
  const [filteredLabels, setFilteredLabels] = useState<LabelData[]>([]);

  // This is the list of applied filter objects displayed under the search bar.
  const appliedFilterItems = useMemo<ArtistAppliedLabelFilter[]>(() => {
    return filteredLabels.map((label) => {
      const { labelId, name, updatedAt, updatedBy } = label;

      return {
        labelId,
        displayName: name,
        updatedAt,
        updatedBy,
      };
    });
  }, [filteredLabels]);

  // Current filter values passed to the filter panel. Note this is different
  // to the filters returned from useRosterUrlParams because these have the
  // full LabelData objects instead of just the labelIds
  const currentFilterValues = useMemo(() => {
    return { labels: filteredLabels, isCrossover, isOnRoster };
  }, [filteredLabels, isCrossover, isOnRoster]);

  const artistTableMetadata = useMemo(() => {
    return {
      filtersCount: labelIds.length,
      filtersApplied: labelIds.length > 0,
      crossoverFilterApplied: isCrossover,
      rosterStatus: getRosterStatus(isOnRoster),
    };
  }, [isCrossover, labelIds.length, isOnRoster]);

  const {
    data: artistsData,
    error,
    isLoading: isLoadingArtists,
    isFetching: isFetchingArtists,
  } = useArtistQuery({
    searchText,
    labelIds,
    isCrossover,
    isOnRoster,
    pageNumber,
  });

  useEffect(() => {
    ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [pageNumber]);

  const totalArtists = artistsData?.pagination.total ?? 0;

  const { data: labelData } = useQuery({
    queryKey: ['labels'],
    queryFn: () => getLabels({}),
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  // Update the filtered label data once the data loads.
  useEffect(() => {
    if (labelIds && labelData) {
      const filteredData = labelData.filter((label: LabelData) =>
        labelIds.includes(label.labelId),
      );

      setFilteredLabels(filteredData);
    }
  }, [labelData, labelIds]);

  const handleSearchInputClear = useCallback(() => {
    // NOTE: Use undefined here to specifically clear the search text from the URL.
    updateFilters({ searchText: undefined });
  }, [updateFilters]);

  const handleSubmitSearch = useCallback(
    (searchValue: string) => {
      trackEvent('rosterArtistSearch', {
        roster_label_filters_count: labelIds.length,
        roster_label_filters_applied: labelIds.length > 0,
        roster_crossover_filter_applied: isCrossover,
        roster_status: getRosterStatus(isOnRoster),
        search_term: searchValue,
      });

      // Make sure to clear the search if it's an empty string. We clear by
      // passing undefined to the updateFilters function.
      updateFilters({ searchText: searchValue || undefined });
    },
    [isCrossover, labelIds.length, isOnRoster, trackEvent, updateFilters],
  );

  const handleRemoveLabelFilter = useCallback(
    (deletedLabelId: number) => {
      const newFilteredLabelData = filteredLabels.filter(
        ({ labelId }) => labelId !== deletedLabelId,
      );

      setFilteredLabels(newFilteredLabelData);

      updateFilters({
        labelIds: newFilteredLabelData.map((label) => label.labelId),
        isCrossover,
      });
    },
    [filteredLabels, isCrossover, updateFilters],
  );

  const handleRemoveCrossoverFilter = useCallback(() => {
    updateFilters({
      isCrossover: false,
    });
  }, [updateFilters]);

  const handleRemoveOnRosterFilter = useCallback(() => {
    updateFilters({
      isOnRoster: undefined,
    });
  }, [updateFilters]);

  const trackPaginationEvent = useCallback(
    (directionToNextPage: boolean) => {
      trackEvent('rosterTablePaginationClick', {
        roster_label_filters_count: labelIds.length,
        roster_crossover_filter_applied: isCrossover,
        roster_label_filters_applied: labelIds.length > 0,
        roster_status: getRosterStatus(isOnRoster),
        direction: directionToNextPage ? 'next_page' : 'previous_page',
      });
    },
    [isCrossover, labelIds.length, isOnRoster, trackEvent],
  );

  const handleApplyFilters = useCallback(
    (appliedFilters: ArtistFiltersType) => {
      setFilteredLabels(appliedFilters.labels);
      setFilters({
        isCrossover: appliedFilters.isCrossover,
        isOnRoster: appliedFilters.isOnRoster,
        labelIds: appliedFilters.labels.map((label) => label.labelId),
        searchText,
      });

      trackEvent('rosterLabelFiltersApplied', {
        roster_label_filters_count: appliedFilters.labels.length,
        roster_crossover_filter_applied: appliedFilters.isCrossover,
        roster_label_filters_applied: appliedFilters.labels.length > 0,
        roster_status: getRosterStatus(appliedFilters.isOnRoster),
      });
    },
    [searchText, setFilters, trackEvent],
  );

  const handleClearFilters = useCallback(() => {
    handleApplyFilters(DEFAULT_ARTIST_FILTERS);
  }, [handleApplyFilters]);

  const handleShowFilters = useCallback(
    (isOpen: boolean) => {
      setFilterPanelIsOpen(isOpen);

      if (isOpen) {
        trackEvent('rosterFiltersClick', {
          roster_label_filters_count: labelIds.length,
          roster_crossover_filter_applied: isCrossover,
          roster_label_filters_applied: labelIds.length > 0,
          roster_status: getRosterStatus(isOnRoster),
        });
      }
    },
    [isCrossover, labelIds.length, isOnRoster, trackEvent],
  );

  const handleAppliedFiltersMoreClick = useCallback(() => {
    setFilterPanelIsOpen((isOpen) => !isOpen);
  }, []);

  useEffect(() => {
    trackEvent('rosterView');
  }, [trackEvent]);

  if (error) {
    return (
      <DataError
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  return (
    <div ref={ref}>
      <Helmet>
        <title>{t('header.heading')}</title>
      </Helmet>
      <div className="sticky -top-xl z-10 -mx-xl -mt-xl bg-gradient bg-[100%,auto] bg-no-repeat px-xl pt-xl bg-level-01/full">
        <div className="flex items-end justify-between py-lg">
          <Header />
          {!isLoadingArtists && !!totalArtists ? (
            <ArtistExport
              search={searchText}
              isCrossover={isCrossover}
              isOnRoster={isOnRoster}
              labels={labelIds}
            />
          ) : null}
        </div>
        <div className="flex gap-md" role="search">
          <ArtistFilterButton
            selectedFilters={currentFilterValues}
            filterPanelIsOpen={filterPanelIsOpen}
            onApplyFilters={handleApplyFilters}
            onShowFilters={handleShowFilters}
          />
          <ArtistSearchBar
            initialSearchText={searchText ?? ''}
            placeholder={t('filters.searchForArtist')}
            onSubmit={handleSubmitSearch}
            onClear={handleSearchInputClear}
          />
        </div>
        {isCrossover || isOnRoster !== undefined || labelIds.length > 0 ? (
          <ArtistAppliedFilterList
            isCrossover={isCrossover}
            isOnRoster={isOnRoster}
            labelFilters={appliedFilterItems}
            onRemoveCrossover={handleRemoveCrossoverFilter}
            onRemoveOnRoster={handleRemoveOnRosterFilter}
            onRemoveLabel={handleRemoveLabelFilter}
            onClear={handleClearFilters}
            onMoreClick={handleAppliedFiltersMoreClick}
          />
        ) : null}
        <div className="my-md inline-flex min-h-xl w-full items-center justify-between">
          <div className="flex gap-xs">
            <h2>{t('header.subheading')}</h2>
            {!isLoadingArtists ? (
              <div className="text-subtle">
                ({totalArtists.toLocaleString()})
              </div>
            ) : null}
          </div>

          {totalArtists > DEFAULT_PAGE_SIZE ? (
            <Pagination
              page={pageNumber}
              total={totalArtists}
              perPage={DEFAULT_PAGE_SIZE}
              entity="artists"
              setPageNumber={(pageNum) => {
                trackPaginationEvent(pageNum > pageNumber);
                setPageNumber(pageNum);
              }}
              showAsLink={true}
            />
          ) : null}
        </div>
      </div>
      <ArtistsTable
        columns={columns}
        meta={artistTableMetadata}
        data={artistsData?.items ?? []}
        isLoading={isFetchingArtists}
      />
    </div>
  );
}

RosterPage.displayName = 'RosterPage';
